export interface Range<T> {
  start: number;
  end: number;
  value: T;
}

export type RangeCombinerFunction<T> = (v1: T, v2: T) => T;

export class RangeList<T> {
  private ranges: Array<Range<T>> = [];
  private defaultValue: T;
  private start: number;
  private end: number;

  constructor(start: number, end: number, defaultValue: T) {
    this.validateRange(start, end);
    this.defaultValue = defaultValue;

    if (end <= start) {
      throw new Error('Range List end must be smaller than start');
    }

    this.start = start;
    this.end = end;

    this.ranges = [{ start: this.start, end: this.end, value: this.defaultValue }];
  }

  public toString() {
    let rangeStr = this.ranges.map(r => this.rangeToString(r)).join(', ');
    return `RangeList[ ${rangeStr} ]`;
  }

  /**
   * Given a range, return all ranges that it overlaps, either fully or partially
   *
   *
   */
  public findOverlaps(range: Range<T>) {
    return this.ranges.filter(r => {
      return !(r.start >= range.end || r.end <= range.start);
    });
  }

  /**
   * Opposite of `findOverlaps`. It returns all ranges that do not overlap with
   * `range`
   *
   */
  public findNonOverlaps(range: Range<T>) {
    return this.ranges.filter(r => {
      return r.start >= range.end || r.end <= range.start;
    });
  }

  public add(start: number, end: number, value: T, combinerF?: RangeCombinerFunction<T>) {
    this.validateRange(start, end);
    if (start < this.start || end > this.end) {
      throw new Error(`Range is out of bounds [${this.start}, ${this.end}]`);
    }

    if (combinerF) {
      return this.combineRange({ start, end, value }, combinerF);
    }

    return this.addRange({ start, end, value });
  }

  public addRangeList(rangeList: RangeList<T>, combinerF: RangeCombinerFunction<T>) {
    let arr = rangeList.toArray();
    arr.forEach(range => this.add(range.start, range.end, range.value, combinerF));
  }

  public toArray() {
    return this.ranges;
  }

  /**
   * Merge all consecutive ranges that have the same value
   *
   */
  public merge() {
    this.ranges = this.mergeRangeArray(this.ranges);
  }

  public clone() {
    let newRangeList = new RangeList(this.start, this.end, this.defaultValue);
    newRangeList.ranges = JSON.parse(JSON.stringify(this.ranges));
    return newRangeList;
  }

  public toCumulativeArray(combinerF: RangeCombinerFunction<T>) {
    // ensure there are at least two elements
    if (this.ranges.length <= 0) return this.toArray();

    // first, add up the values for all the ranges
    let acc = this.ranges[0].value;
    let arr = this.ranges.map(r => {
      acc = combinerF(acc, r.value);
      let range = {
        start: r.start,
        end: r.end,
        value: acc,
      };
      return range;
    });

    return this.mergeRangeArray(arr);
  }

  /**
   * Like `addRange`, but the new range's value is combined with the existing
   * value rather than overriding them
   *
   */
  private combineRange(range: Range<T>, combinerF: RangeCombinerFunction<T>) {
    let overlaps = this.findOverlaps(range);
    let newRanges: Array<Range<T>> = [];

    // for each overlap, distribute them in categories

    // 1- partial overlap at the end of the range
    let endOverlap = overlaps.filter(r => r.start <= range.start && r.end < range.end);
    endOverlap.forEach(r => {
      if (r.start !== range.start) {
        newRanges.push({ start: r.start, end: range.start, value: r.value });
      }
      newRanges.push({ start: range.start, end: r.end, value: combinerF(range.value, r.value) });
    });

    // 2- partial overlap at the beginning of the range
    let startOverlap = overlaps.filter(r => r.start > range.start && r.end >= range.end);
    startOverlap.forEach(r => {
      newRanges.push({ start: r.start, end: range.end, value: combinerF(range.value, r.value) });
      if (r.end !== range.end) {
        newRanges.push({ start: range.end, end: r.end, value: r.value });
      }
    });

    // 3- full overlap, the existing range is inside the new one (but not exactly the same)
    let overlapInside = overlaps.filter(r => r.start > range.start && r.end < range.end);
    overlapInside.forEach(r => {
      newRanges.push({ start: r.start, end: r.end, value: combinerF(range.value, r.value) });
    });

    // 4- full overlap, the new range falls between the existing one
    let fullOverlap = overlaps.filter(r => r.start <= range.start && r.end >= range.end);
    fullOverlap.forEach(r => {
      if (r.start !== range.start) {
        newRanges.push({ start: r.start, end: range.start, value: r.value });
      }

      newRanges.push({
        start: range.start,
        end: range.end,
        value: combinerF(range.value, r.value),
      });

      if (r.end !== range.end) {
        newRanges.push({ start: range.end, end: r.end, value: r.value });
      }
    });

    let nonOverlaps = this.findNonOverlaps(range);
    this.ranges = nonOverlaps.concat(newRanges);
    this.sortRanges();
  }

  /**
   * Helper for `merge` that acts on the array directly
   */
  private mergeRangeArray(arr: Array<Range<T>>) {
    let merged: Array<Range<T>> = [];

    if (arr.length === 0) return merged;

    // Keep track of the previous range we saw
    let prevRange = arr[0];

    // then, merge those which have the same values
    arr.slice(1).forEach(r => {
      // If we find a different value, add a new range that starts at the
      // previous range and ends where this one starts. Otherwise, skip the
      // current range and move on
      if (r.value !== prevRange.value) {
        merged.push({ start: prevRange.start, end: r.start, value: prevRange.value });
        prevRange = r;
      }
    });

    // Add the last one if we haven't done so
    let lastRange = arr[arr.length - 1];
    if (lastRange.value === prevRange.value) {
      merged.push({ start: prevRange.start, end: lastRange.end, value: lastRange.value });
    }

    return merged;
  }

  private rangeToString(r: Range<T>) {
    return `${r.start}-${r.end}(${r.value})`;
  }

  private validateRange(start: number, end: number) {
    if (!Number.isInteger(start)) throw new Error('Must provide a valid start value');
    if (!Number.isInteger(end)) throw new Error('Must provide a valid end value');
  }

  private sortRanges() {
    this.ranges.sort((x, y) => x.start - y.start);
  }

  /**
   * Create new ranges when there are overlapping ranges
   *
   * This also assumes there are no conflicts in the range list
   *
   * NOTE: We could express this using `_combineRange` like this:
   *           this._combineRange(range, (newV, oldV) => newV)
   *       However, this does not work perfectly because we'd need to call
   *       `merge` afterwards to ensure the results are close. But `_addRange`
   *       does not merge in all cases!
   */
  private addRange(range: Range<T>) {
    // TODO: This can be improved quite a lot
    let newRanges: Array<Range<T>> = [];
    this.ranges.forEach(r => {
      if (r.start >= range.end || r.end <= range.start) {
        // No overlap, just keep in the list of ranges
        newRanges.push(r);
      } else {
        // Overlap
        if (r.start >= range.start && r.end <= range.end) {
          // Full overlap, the range is removed from the new list, so do nothing
        } else {
          // Partial overlap, we need to break into new ranges
          if (r.start < range.start) {
            // Overlap at the end
            newRanges.push({ start: r.start, end: range.start, value: r.value });
          }
          if (r.end > range.end) {
            // Overlap at the beginning
            newRanges.push({ start: range.end, end: r.end, value: r.value });
          }
        }
      }
    });

    // Finally, add the new range
    newRanges.push(range);

    this.ranges = newRanges;
    this.sortRanges();
  }
}
